
import { addTeacherNotification, getNotificationById, getDB } from './db-operations';
import { TeacherNotification } from './types';
import { Teacher } from '@/types/teacher.types';

// Generate document expiration notification
export const generateDocumentNotification = async (
  teacher: Teacher, 
  documentType: string,
  expirationDate: Date
): Promise<string | null> => {
  try {
    if (!teacher || !teacher.id) return null;
    
    const today = new Date();
    const daysUntilExpiration = Math.floor((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    let title: string;
    let message: string;
    
    if (daysUntilExpiration <= 0) {
      title = `${documentType} Has Expired`;
      message = `Your ${documentType} expired on ${expirationDate.toLocaleDateString()}.`;
    } else if (daysUntilExpiration <= 30) {
      title = `${documentType} Expiring Soon`;
      message = `Your ${documentType} will expire in ${daysUntilExpiration} days on ${expirationDate.toLocaleDateString()}.`;
    } else {
      return null; // No notification needed yet
    }
    
    const notification: TeacherNotification = {
      id: crypto.randomUUID(),
      teacherId: teacher.id,
      type: 'document',
      contentType: 'contract-expiration',
      title,
      message,
      createdAt: new Date(),
      isRead: false,
      read: false,
      date: today.toISOString(),
      metadata: {
        documentType,
        expirationDate
      }
    };
    
    return addTeacherNotification(notification);
  } catch (error) {
    console.error('Error generating document notification:', error);
    return null;
  }
};

// Check for contract expirations and create notifications
export const checkContractExpirations = async (): Promise<TeacherNotification[]> => {
  try {
    const db = await getDB();
    const teachers = await db.getAll('teachers');
    const notifications: TeacherNotification[] = [];
    
    for (const teacher of teachers) {
      if (teacher.contractEndDate) {
        const endDate = new Date(teacher.contractEndDate);
        const today = new Date();
        const daysRemaining = Math.floor((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysRemaining <= 30 && daysRemaining >= 0) {
          // Create a notification
          const notificationId = await generateDocumentNotification(
            teacher, 
            'Employment Contract', 
            endDate
          );
          
          if (notificationId) {
            const notification = await getNotificationById(notificationId);
            if (notification) notifications.push(notification);
          }
        }
      }
    }
    
    return notifications;
  } catch (error) {
    console.error('Error checking for contract expirations:', error);
    return [];
  }
};
